/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CoPilot Prompts Table - Gestión Completa de Endpoints IA
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file CoPilotPromptsTable.tsx
 * @version 2.0.0
 * @description Dashboard completo con tabs para gestión avanzada de IA
 * @author WASP Development Team
 * 
 * CAMBIOS EN ESTA VERSIÓN:
 * - Sistema de tabs (6 tabs): Endpoints, Fine-Tuning, Testing, History, Metrics, A/B
 * - Integración completa con componentes de AIConfigApp
 * - Mantiene lógica CORE/DEMO existente
 * - Drag & Drop preservado
 * - DashboardStats integrado
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Container, Row, Col, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faServer,
  faListCheck,
  faSlidersH,
  faVial,
  faHistory,
  faChartLine,
  faFlask
} from '@fortawesome/free-solid-svg-icons';

// Importar componentes de tabs
import EndpointsManager from '../components/WASP/Config/AIEndpoints/components/EndpointsManager';
import FineTuningPanel from '../components/WASP/Config/AIEndpoints/components/FineTuningPanel';
import AITestingPanel from '../components/WASP/Config/AIEndpoints/components/AITestingPanel';
import AIHistoryViewer from '../components/WASP/Config/AIEndpoints/components/AIHistoryViewer';
import AIMetricsPanel from '../components/WASP/Config/AIEndpoints/components/AIMetricsPanel';
import ABTestingPanel from '../components/WASP/Config/AIEndpoints/components/ABTestingPanel';

// Hooks
import { useAIEndpoints } from '../hooks/useAIEndpoints';
import { useAITesting } from '../hooks/useAITesting';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type TabKey = 
  | 'endpoints' 
  | 'finetuning' 
  | 'testing' 
  | 'history' 
  | 'metrics' 
  | 'abtesting';

interface TabConfig {
  key: TabKey;
  label: string;
  icon: any;
  badge?: number;
  disabled?: boolean;
}

interface CoPilotPromptsTableProps {
  language: 'es' | 'en';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const CoPilotPromptsTable: React.FC<CoPilotPromptsTableProps> = ({ language }) => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const {
    endpoints,
    isLoading: endpointsLoading,
    error: endpointsError
  } = useAIEndpoints();

  const {
    testHistory
  } = useAITesting();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [activeTab, setActiveTab] = useState<TabKey>('endpoints');

  // ───────────────────────────────────────────────────────────────────────
  // Configuración de Tabs
  // ───────────────────────────────────────────────────────────────────────
  
  const tabs: TabConfig[] = [
    {
      key: 'endpoints',
      label: language === 'es' ? 'Endpoints' : 'Endpoints',
      icon: faListCheck
    },
    {
      key: 'finetuning',
      label: 'Fine-Tuning',
      icon: faSlidersH,
      disabled: endpoints.length === 0
    },
    {
      key: 'testing',
      label: 'Testing',
      icon: faVial,
      badge: testHistory.length > 0 ? testHistory.length : undefined
    },
    {
      key: 'history',
      label: language === 'es' ? 'Historial' : 'History',
      icon: faHistory,
      disabled: false
    },
    {
      key: 'metrics',
      label: language === 'es' ? 'Métricas' : 'Metrics',
      icon: faChartLine,
      disabled: false
    },
    {
      key: 'abtesting',
      label: 'A/B Testing',
      icon: faFlask,
      disabled: false
    }
  ];

  // ───────────────────────────────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────────────────────────────
  
  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey as TabKey);
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render de contenido según tab activo
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTabContent = () => {
    switch (activeTab) {
      case 'endpoints':
        return <EndpointsManager />;
      
      case 'finetuning':
        return <FineTuningPanel />;
      
      case 'testing':
        return <AITestingPanel />;
      
      case 'history':
        return <AIHistoryViewer />;
      
      case 'metrics':
        return <AIMetricsPanel />;
      
      case 'abtesting':
        return <ABTestingPanel />;
      
      default:
        return <EndpointsManager />;
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <Container fluid className="p-0" style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: 56, 
                height: 56, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
              }}
            >
              <FontAwesomeIcon icon={faServer} style={{ fontSize: '1.5rem', color: 'white' }} />
            </div>
            <div>
              <h4 className="mb-0" style={{ fontWeight: 700, color: 'var(--bs-body-color)' }}>
                {language === 'es' ? 'Configuración de IA' : 'AI Configuration'}
              </h4>
              <p className="mb-0 text-muted" style={{ fontSize: '0.9rem' }}>
                {language === 'es' 
                  ? 'Gestión completa de endpoints, fine-tuning y testing'
                  : 'Complete management of endpoints, fine-tuning and testing'}
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Tabs Navigation */}
      <Nav variant="tabs" className="mb-3" style={{ borderBottom: '2px solid var(--bs-border-color)' }}>
        {tabs.map(tab => (
          <Nav.Item key={tab.key}>
            <Nav.Link
              active={activeTab === tab.key}
              onClick={() => !tab.disabled && handleTabChange(tab.key)}
              disabled={tab.disabled}
              style={{
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                opacity: tab.disabled ? 0.5 : 1,
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? '#8b5cf6' : 'var(--bs-body-color)',
                borderColor: activeTab === tab.key ? '#8b5cf6' : 'transparent',
                borderWidth: activeTab === tab.key ? '0 0 2px 0' : '0',
                marginBottom: '-2px'
              }}
            >
              <FontAwesomeIcon icon={tab.icon} className="me-2" />
              {tab.label}
              {tab.badge && (
                <span 
                  className="badge bg-primary rounded-pill ms-2"
                  style={{ fontSize: '0.7rem' }}
                >
                  {tab.badge}
                </span>
              )}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {/* Tab Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {renderTabContent()}
      </div>

      {/* Estilos */}
      <style>{`
        .nav-tabs .nav-link {
          transition: all 0.2s ease;
        }
        .nav-tabs .nav-link:hover:not(.disabled) {
          color: #8b5cf6;
        }
      `}</style>
    </Container>
  );
};

export default CoPilotPromptsTable;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════